const starCount = (overview, score) =>
    Number(overview?.[`feedback_score_${score}_count`]) || 0;

/** Mean of the same 0–5 star buckets shown in the overview bar chart. */
export const averageScoreFromOverview = (overview) => {
    let sum = 0;
    let count = 0;
    for (let score = 0; score <= 5; score += 1) {
        const n = starCount(overview, score);
        sum += score * n;
        count += n;
    }
    if (!count) return null;
    return Math.round((sum / count) * 10) / 10;
};

/** Qualtrics-style CSAT: share of 4+5 ratings, as a whole percent. */
export const satisfactionPercentFromOverview = (overview) => {
    const total =
        Number(overview?.feedback_total_count) ||
        [0, 1, 2, 3, 4, 5].reduce((sum, score) => sum + starCount(overview, score), 0);
    if (!total) return 0;
    return Math.round(
        ((starCount(overview, 4) + starCount(overview, 5)) / total) * 100
    );
};

const npsNorm = (score) =>
    score == null ? null : Math.round((score + 100) * 5) / 10;

const compositeIndex = (parts) => {
    let total = 0;
    let weight = 0;
    parts.forEach(([value, partWeight]) => {
        if (value == null) return;
        total += value * partWeight;
        weight += partWeight;
    });
    if (!weight) return null;
    return Math.round((total / weight) * 10) / 10;
};

export const cxFromScoreOverview = (overview) => {
    const promoters = starCount(overview, 5);
    const passives = starCount(overview, 4);
    const detractors =
        starCount(overview, 3) +
        starCount(overview, 2) +
        starCount(overview, 1) +
        starCount(overview, 0);
    const sample = promoters + passives + detractors;
    const csatSample =
        starCount(overview, 5) +
        starCount(overview, 4) +
        starCount(overview, 3) +
        starCount(overview, 2) +
        starCount(overview, 1);
    const satisfied = starCount(overview, 4) + starCount(overview, 5);
    const weighted =
        5 * starCount(overview, 5) +
        4 * starCount(overview, 4) +
        3 * starCount(overview, 3) +
        2 * starCount(overview, 2) +
        starCount(overview, 1);
    const npsScore = sample
        ? Math.round(((promoters - detractors) * 100) / sample)
        : null;
    const csatScore = csatSample
        ? Math.round((satisfied * 1000) / csatSample) / 10
        : null;
    return {
        nps: {
            score: npsScore,
            sample,
        },
        csat: {
            score: csatScore,
            average: csatSample
                ? Math.round((weighted / csatSample) * 10) / 10
                : null,
            sample: csatSample,
        },
        loyalty: { score: null, advocates: 0, advocate_rate: null },
        engagement: { score: null, active: 0, sample: 0 },
        enps: { score: null, sample: 0 },
        ces: { score: null, average: null, sample: 0 },
        cx_index: {
            score: compositeIndex([
                [npsNorm(npsScore), 0.4],
                [csatScore, 0.4],
            ]),
            sample: Math.max(sample, csatSample),
        },
        ex_index: { score: null, sample: 0 },
    };
};

export const countByStatus = (feedback, status) =>
    Number(
        (Array.isArray(feedback) ? feedback : []).find(
            (item) => item.status === status
        )?.feedbacks
    ) || 0;

export const totalStatusCount = (feedback) =>
    Array.isArray(feedback)
        ? feedback.reduce((sum, item) => sum + (Number(item.feedbacks) || 0), 0)
        : 0;

export const countTreeNodes = (nodes) => {
    if (!Array.isArray(nodes)) return 0;
    return nodes.reduce(
        (sum, node) => sum + 1 + countTreeNodes(node.children),
        0
    );
};

export const listCount = (payload) => {
    if (Array.isArray(payload)) return payload.length;
    if (payload && typeof payload.count === "number") return payload.count;
    if (Array.isArray(payload?.results)) return payload.results.length;
    return 0;
};

const hasValue = (value) => {
    if (value == null) return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
};

export const profileFieldCount = (company) => {
    const fields = [
        company?.name,
        company?.logo,
        company?.phone_number,
        company?.email,
        company?.address,
    ];
    return fields.filter(hasValue).length;
};

export const isProfileComplete = (company) =>
    hasValue(company?.name) && profileFieldCount(company) >= 4;
